/**
 * The heart.
 *
 * Optimistic: the icon flips immediately and reverts if the request fails. A favorite is
 * a trivial, reversible action, and waiting a round-trip to acknowledge a tap is the
 * difference between an app that feels responsive and one that does not.
 *
 * Not shown for episodes — the server refuses them, because the unit a viewer means by
 * "favorite" is the show.
 */
import { useEffect, useState } from 'react';
import type { CatalogItemDto } from '@filmodasi/shared';
import { api } from '../api/client';
import { Icon } from './Icon';

export function FavoriteButton({ item }: { item: CatalogItemDto }) {
  const [favorited, setFavorited] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  const favoritable = item.type === 'live' || item.type === 'movie' || item.type === 'series';

  useEffect(() => {
    if (!favoritable) return;
    let cancelled = false;
    void api<{ favorited: boolean }>(`/api/favorites/${item.id}/status`)
      .then((r) => {
        if (!cancelled) setFavorited(r.favorited);
      })
      .catch(() => {
        // An unknown state renders as "not favorited" rather than as an error: this is
        // an accessory control, and a broken heart icon is worse than a neutral one.
        if (!cancelled) setFavorited(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item.id, favoritable]);

  if (!favoritable || favorited === null) return null;

  const toggle = async () => {
    if (busy) return;
    const next = !favorited;
    setFavorited(next);
    setBusy(true);
    try {
      if (next) await api(`/api/favorites/${item.id}`, { method: 'PUT' });
      else await api(`/api/favorites/${item.id}`, { method: 'DELETE' });
    } catch {
      setFavorited(!next); // revert; the server is the authority
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={`fav${favorited ? ' fav--on' : ''}`}
      onClick={() => void toggle()}
      aria-pressed={favorited}
      title={favorited ? 'Favorilerden çıkar' : 'Favorilere ekle'}
    >
      {/* Two glyphs — `♥` and `♡` — until now, which is the shape of the problem the icon
          set exists to solve: they are different widths in most system fonts, so the label
          beside them shifted sideways every time the heart was toggled. */}
      <Icon name={favorited ? 'heartFilled' : 'heart'} size={1.05} />
      <span>{favorited ? 'Favorilerde' : 'Favorilere ekle'}</span>
    </button>
  );
}
